/**
 * `ProposalVoteUpdate` → proposal-row `upvotes` / `downvotes` / `netVotes` mapper.
 *
 * Subject `morfi.market.${m}.votes.v0` (proto
 * `morfi.v1.cmd.ProposalVoteUpdate`) carries POST-mutation aggregates so
 * subscribers don't need to round-trip back to the API to repaint vote
 * counts. The helper writes all three derived fields off one event:
 *
 *   upvotes   ← raw.upvotes
 *   downvotes ← raw.downvotes
 *   netVotes  ← upvotes - downvotes
 *
 * Generic over any row shape carrying the three fields — the codegen-emitted
 * caller passes the concrete `GetProposalsRow`.
 *
 * Note: `myVote` (the viewer's own vote) is NOT touched here — it's patched
 * optimistically by the `useSetProposalVote` / `useClearProposalVote`
 * mutations and is per-viewer, not per-broadcast. The realtime event
 * intentionally carries the voter identity but no `voterPubkey` matching
 * against the viewer happens in the helper (the SDK has no viewer-id
 * threading into reducers in v0).
 *
 * Immutable: returns a NEW object only when at least one of the three
 * counts actually changes; otherwise the exact `prev` reference.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

function coerceCount(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.trunc(v);
  if (typeof v === 'bigint') {
    if (v < 0n) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!/^\d+$/.test(trimmed)) return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function recomputeNetVotes<
  T extends {
    readonly upvotes?: number;
    readonly downvotes?: number;
    readonly netVotes?: number;
  },
>(prev: T, raw: Record<string, unknown>): T {
  const upvotes = coerceCount(raw.upvotes);
  const downvotes = coerceCount(raw.downvotes);
  const netVotes = upvotes - downvotes;

  if (prev.upvotes === upvotes && prev.downvotes === downvotes && prev.netVotes === netVotes) {
    return prev;
  }
  return { ...prev, upvotes, downvotes, netVotes };
}
